import { useEffect } from "react";
import { useSelector } from "react-redux";

export const DEFAULT_BRAND_NAME = "Pollux";

const STORAGE_KEY = "brandName";

// The configured name only arrives with the async config fetch, so the first paint would
// otherwise render the fallback and visibly swap. These resolve synchronously: the last name
// this browser actually saw, then the value the container injected at startup.
const cachedBrandName = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || "";
    } catch (e) {
        return "";
    }
};

const injectedBrandName = () => (typeof window !== "undefined" && window.RUNTIME_CONFIG?.BRAND_NAME) || "";

export const rememberBrandName = (brandName) => {
    if (!brandName) return;
    try {
        window.localStorage.setItem(STORAGE_KEY, brandName);
    } catch (e) {
        // Private browsing or a full quota — the rest of the chain still resolves.
    }
};

// Synchronous best guess for code that runs outside React.
export const resolveBrandName = () => cachedBrandName() || injectedBrandName() || DEFAULT_BRAND_NAME;

export const useBrandName = () => {
    const configured = useSelector((state) => state.config.brandName);
    return configured || resolveBrandName();
};

// Sets the tab title to "<page> - <brand>". The cleanup restores the bare brand name so a
// page that never sets a title doesn't inherit the previous one: on navigation React runs
// the old component's cleanup before the new one's effect.
export const usePageTitle = (title) => {
    const brandName = useBrandName();
    const pageTitle = typeof title === "string" ? title.trim() : "";

    useEffect(() => {
        document.title = pageTitle ? `${pageTitle} - ${brandName}` : brandName;
        return () => {
            document.title = brandName;
        };
    }, [pageTitle, brandName]);
};
